import React, {Suspense, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {ERROR} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import DrivingInfoAdas from '@di-shared/features/history/driving-info/DrivingInfoAdas';
import DrivingInfoDistance from '@di-shared/features/history/driving-info/DrivingInfoDistance';
import {DRIVING_DISTANCE_GRAPH_KR} from '@di-shared/features/history/driving-info/DrivingInfoDistance/.kr';
import DrivingInfoFuel from '@di-shared/features/history/driving-info/DrivingInfoFuel';
import {FUEL_EFFICIENCY_GRAPH_KR} from '@di-shared/features/history/driving-info/DrivingInfoFuel/.kr';
import DrivingInfoHeader from '@di-shared/features/history/driving-info/DrivingInfoHeader';
import DrivingInfoHeaderSettingTooltip from '@di-shared/features/history/driving-info/DrivingInfoHeaderSettingTooltip';
import DrivingInfoTrip from '@di-shared/features/history/driving-info/DrivingInfoTrip';
import EnergyUsageDaily from '@di-shared/features/history/energy-usage/EnergyUsageDaily';
import EnergyUsageRecent from '@di-shared/features/history/energy-usage/EnergyUsageRecent';
import {HistoryProps} from '@di-shared/features/history/index';
import HistoryLock from '@di-shared/features/history/setting/HistoryLock';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {HistoryTab} from '@di-shared/pages/[vin]/history';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {isSameYearAndMonth, parseDateWithFormat, startDateOfMonth, subDate} from '@shared/dateUtils';

export default function ({vin, tab, changeTab, month, changeMonth, date, changeDate}: HistoryProps) {
  const selectedDate = parseDateWithFormat(month) ?? new Date();

  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <HistoryLock
          vin={vin}
          fallbackLoading={<FallbackLoading />}
          fallbackError={<FallbackError selectedDate={selectedDate} />}
        >
          <Inner
            vin={vin}
            tab={tab}
            changeTab={changeTab}
            month={month}
            changeMonth={changeMonth}
            date={date}
            changeDate={changeDate}
          />
        </HistoryLock>
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, tab, changeTab, month, changeMonth}: HistoryProps) {
  /**
   * [2026-03-09:Jeyoung Lee] FIXME: date, changeDate props 추가 시 EU와 동일한 로직 적용 필요
   */
  const {t} = useInferableTranslation();
  const {checkAvailableComponent} = useAvailableComponentKr({vin});
  const [selectedDate, setSelectedDate] = useState(parseDateWithFormat(month) ?? new Date());

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_absolute"
        left={<HeaderBackButton />}
        right={<DrivingInfoHeaderSettingTooltip vin={vin} />}
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T10000')}</h1>
      </Header>
      <main className="content page_my_trips">
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<ConsentBannerLoadingKr />}>
            <ConsentBannerKr vin={vin} />
          </Suspense>
        </ErrorBoundary>
        {checkAvailableComponent(PermissionTypeKr.EV_ENERGY_USAGE) && (
          <div
            className="tabbar_txt tabbar_sticky"
            role="tablist"
          >
            <div className="inner">
              <button
                type="button"
                className={clsx('tabbar_item body_large_r', {'is-selected': tab === HistoryTab.DRIVING_INFO})}
                onClick={() => changeTab(HistoryTab.DRIVING_INFO)}
                id={`${HistoryTab.DRIVING_INFO}_tab`}
                role="tab"
                aria-selected={tab === HistoryTab.DRIVING_INFO}
                aria-controls={`${HistoryTab.DRIVING_INFO}_tabpanel`}
                tabIndex={tab === HistoryTab.DRIVING_INFO ? 0 : -1}
              >
                <span className="txt">{t('T10001')}</span>
              </button>
              <button
                type="button"
                className={clsx('tabbar_item body_large_r', {'is-selected': tab === HistoryTab.EV_ENERGY_USAGE})}
                onClick={() => changeTab(HistoryTab.EV_ENERGY_USAGE)}
                id={`${HistoryTab.EV_ENERGY_USAGE}_tab`}
                role="tab"
                aria-selected={tab === HistoryTab.EV_ENERGY_USAGE}
                aria-controls={`${HistoryTab.EV_ENERGY_USAGE}_tabpanel`}
                tabIndex={tab === HistoryTab.EV_ENERGY_USAGE ? 0 : -1}
              >
                <span className="txt">{t('T10002')}</span>
              </button>
            </div>
          </div>
        )}
        {tab === HistoryTab.DRIVING_INFO ? (
          <div
            className="tab_content monthly_driving"
            id={`${HistoryTab.DRIVING_INFO}_tabpanel`}
            aria-labelledby={`${HistoryTab.DRIVING_INFO}_tab`}
          >
            <section className="section">
              <DrivingInfoHeader
                vin={vin}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                changeMonth={changeMonth}
              />
              <DrivingInfoDistance
                vin={vin}
                selectedDate={selectedDate}
              />
              {checkAvailableComponent(PermissionTypeKr.FUEL_EFFICIENCY) && (
                <DrivingInfoFuel
                  vin={vin}
                  selectedDate={selectedDate}
                />
              )}
              <DrivingInfoAdas
                vin={vin}
                selectedDate={selectedDate}
              />
              {checkAvailableComponent(PermissionTypeKr.TRIPS_RECENT) && (
                <DrivingInfoTrip
                  vin={vin}
                  selectedDate={selectedDate}
                />
              )}
            </section>
          </div>
        ) : (
          <div
            className="tab_content energy_usage"
            id={`${HistoryTab.EV_ENERGY_USAGE}_tabpanel`}
            aria-labelledby={`${HistoryTab.EV_ENERGY_USAGE}_tab`}
          >
            <EnergyUsageRecent vin={vin} />
            <div className="divider divider_thick" />
            <EnergyUsageDaily vin={vin} />
          </div>
        )}
      </main>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol, defaultFuelEfficiencySymbol} = useUnitConverter();
  const today = new Date();
  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_KR.LENGTH).keys()).reverse();

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_with_tab"
        left={<HeaderBackButton />}
        right={
          <button
            type="button"
            className="btn btn_ic"
            aria-label={t('T10005')}
          >
            <span
              className="ic ic_setting_24px"
              aria-hidden="true"
            />
          </button>
        }
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T10000')}</h1>
      </Header>
      <main className="content page_my_trips">
        <div className="tab_content monthly_driving">
          <section className="section">
            <div className="section_heading type_main">
              <button
                type="button"
                className="btn_monthly body_medium_b skeleton"
                style={{width: '160px', height: '20px'}}
              />
              <h3
                className="section_heading_title skeleton"
                style={{width: '200px', height: '28px'}}
              />
              <span
                className="section_heading_caption skeleton"
                style={{width: '160px', height: '20px'}}
              />
            </div>
            <div className="section_container skeleton">
              <div className="section_container_header">
                <h4 className="title_xsmall_b">
                  {t('T10038_$month_$dist', {
                    month: CldrDatetimeFormatter.format(today, 'MMM'),
                    dist: ERROR.DASH + defaultDistanceSymbol,
                  })}
                </h4>
              </div>
              <div className="statistics_graph_area distance_statistics">
                <div className="bar_graph">
                  {errorDrivingDistances.map((month) => {
                    const legendDate = subDate(today, {months: month});
                    const isSelectedMonth = isSameYearAndMonth(legendDate, today);
                    return (
                      <div
                        key={month}
                        className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                      >
                        {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                        <span
                          className="graph_bar"
                          style={{height: `${DRIVING_DISTANCE_GRAPH_KR.MIN_HEIGHT}px`}}
                        />
                        <span className="graph_title">{CldrDatetimeFormatter.format(legendDate, 'MMM')}</span>
                      </div>
                    );
                  })}
                  <div
                    className="bar_graph_average"
                    style={{top: `${DRIVING_DISTANCE_GRAPH_KR.AREA_HALF}px`}}
                  >
                    <span className="average_line" />
                    <span className="average_text_box">
                      <span className="average_text">{t('T10083')}</span>
                      <span className="average_value">{ERROR.DASH}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="driving_banner">
                <div className="driving_banner_box">
                  <span className="driving_banner_txtbox">
                    <strong className="driving_banner_title body_medium_b">{t('T10068')}</strong>
                    <span className="driving_banner_desc body_small_r">{t('T10069_$mileage', {mileage: 6})}</span>
                  </span>
                  <span className="driving_banner_imgbox">
                    <ImageSelector
                      className="driving_banner_img"
                      alt="driving_banner_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage.png')}
                    />
                  </span>
                </div>
              </div>
            </div>
            <div className="section_container skeleton">
              <div className="section_container_header">
                <h4 className="title_xsmall_b">
                  {t('T10082_$month_$fuel', {
                    month: CldrDatetimeFormatter.format(today, 'MMM'),
                    fuel: ERROR.DASH + defaultFuelEfficiencySymbol,
                  })}
                </h4>
              </div>
              <div className="statistics_graph_area efficiency_statistics">
                <div className="bar_graph">
                  <div className="bar_graph_item">
                    <span
                      className="graph_bar"
                      style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
                    />
                    <span className="graph_title">{t('T10084')}</span>
                  </div>
                  <div className="bar_graph_item">
                    <span
                      className="graph_bar"
                      style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
                    />
                    <span className="graph_title">{t('T10085_$percent', {percent: 10})}</span>
                  </div>
                  <div className="bar_graph_item is-active">
                    <span className="speech_bubble">{ERROR.DASH}</span>
                    <span
                      className="graph_bar"
                      style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
                    />
                    <span className="graph_title">{t('T10086')}</span>
                  </div>
                  <div
                    className="bar_graph_average"
                    style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.AREA_HALF}px`}}
                  >
                    <span className="average_line" />
                    <span className="average_text_box">
                      <span className="average_text">{t('T10083')}</span>
                      <span className="average_value">{ERROR.DASH}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="section_container skeleton">
              <h4
                style={{whiteSpace: 'pre-wrap'}}
                className="title_xxsmall"
              >
                {t('T10027')}
              </h4>
              <ul className="device_info_list">
                <li className="device_info_item">
                  <span className="device_info_icon">
                    <span className="ic ic_svm_front_24px" />
                  </span>
                  <span className="device_info_key body_large_r">{t('T10028')}</span>
                  <span className="device_info_val body_xlarge_b">{t('T10014_$count', {count: ERROR.DASH})}</span>
                </li>
                <li className="device_info_item">
                  <span className="device_info_icon">
                    <span className="ic ic_stability_control_24px" />
                  </span>
                  <span className="device_info_key body_large_r">{t('T10029')}</span>
                  <span className="device_info_val body_xlarge_b">{t('T10014_$count', {count: ERROR.DASH})}</span>
                </li>
                <li className="device_info_item">
                  <span className="device_info_icon">
                    <span className="ic ic_cruise_control_20px" />
                  </span>
                  <span className="device_info_key body_large_r">{t('T10030')}</span>
                  <span className="device_info_val body_xlarge_b">{t('T10014_$count', {count: ERROR.DASH})}</span>
                </li>
              </ul>
              <div className="driving_banner type_small">
                <div className="driving_banner_box">
                  <span className="driving_banner_txtbox">
                    <strong className="driving_banner_title body_small_b">{t('T10032')}</strong>
                  </span>
                  <span className="driving_banner_imgbox">
                    <ImageSelector
                      className="driving_banner_img"
                      alt="driving_banner_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                    />
                  </span>
                </div>
              </div>
            </div>
            <div className="section_container skeleton">
              <div className="section_container_header">
                <h4 className="title_xxsmall js-tooltip-left-width">{t('T10040')}</h4>
              </div>
              <ReactDatePicker
                inline
                calendarClassName={'show-header'}
                locale={getDateFnsLocale()}
                disabledKeyboardNavigation
                onChange={() => {}}
                renderCustomHeader={() => null}
              />
              <ReactDatePicker
                inline
                calendarClassName={`show-first-week-1`}
                locale={getDateFnsLocale()}
                disabledKeyboardNavigation
                onChange={() => null}
                weekDayClassName={() => 'react-datepicker--disabled'}
                renderCustomHeader={() => null}
              />
              <div className="trip_summary">
                <div className="trip_date skeleton">
                  <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
                  <span className="trip_date_today">{t('T10008')}</span>
                </div>
                <div className="trip_summary_list type_error skeleton">
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('T10052')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
                    </div>
                  </div>
                  <div className="divider divider_vertical" />
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('T10053')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{t('T10054')}</span>
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{t('T10055')}</span>
                    </div>
                  </div>
                  <div className="divider divider_vertical" />
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('T10057')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{t('T10056')}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="trip_info skeleton"
                style={{height: '126px'}}
              >
                <div className="empty_area">
                  <span className="ic ic_caution_32px" />
                  <span
                    className="body_small_r"
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    {t('T10175')}
                  </span>
                </div>
              </div>
              <button
                className="btn btn_large btn_filled btn_full"
                type="button"
              >
                {t('T10060_$month', {month: ''})}
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol, defaultFuelEfficiencySymbol} = useUnitConverter();
  const startDate = startDateOfMonth(selectedDate);
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_KR.LENGTH).keys()).reverse();

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_with_tab"
        left={<HeaderBackButton />}
        right={
          <button
            type="button"
            className="btn btn_ic"
            aria-label={t('T10005')}
          >
            <span
              className="ic ic_setting_24px"
              aria-hidden="true"
            />
          </button>
        }
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T10000')}</h1>
      </Header>
      <main className="content page_my_trips">
        <div className="tab_content monthly_driving">
          <section className="section">
            <div className="section_heading type_main">
              <button
                type="button"
                className="btn_monthly body_medium_b"
              >
                {CldrDatetimeFormatter.format(startDate, 'yMMM')}
                <span
                  className="ic ic_arrow_down_16px"
                  aria-hidden="true"
                />
              </button>
              <h3 className="section_heading_title">{t('T10003')}</h3>
              <span className="section_heading_caption">{t('T10004_$date', {date: ERROR.DASH})}</span>
            </div>
            <div className="section_visual">
              <ImageSelector
                alt="section_visual_img"
                className="section_visual_img"
                kiaLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
              />
            </div>
            <div className="section_container">
              <div className="section_container_header">
                <h4 className="title_xsmall_b">
                  {t('T10038_$month_$dist', {
                    month: CldrDatetimeFormatter.format(startDate, 'MMM'),
                    dist: ERROR.DASH + defaultDistanceSymbol,
                  })}
                </h4>
              </div>
              <div className="statistics_graph_area distance_statistics">
                <div className="bar_graph">
                  {errorDrivingDistances.map((month) => {
                    const legendDate = subDate(startDate, {months: month});
                    const isSelectedMonth = isSameYearAndMonth(legendDate, startDate);
                    return (
                      <div
                        key={month}
                        className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                      >
                        {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                        <span
                          className="graph_bar"
                          style={{height: `${DRIVING_DISTANCE_GRAPH_KR.MIN_HEIGHT}px`}}
                        />
                        <span className="graph_title">{CldrDatetimeFormatter.format(legendDate, 'MMM')}</span>
                      </div>
                    );
                  })}
                  <div
                    className="bar_graph_average"
                    style={{top: `${DRIVING_DISTANCE_GRAPH_KR.AREA_HALF}px`}}
                  >
                    <span className="average_line" />
                    <span className="average_text_box">
                      <span className="average_text">{t('T10083')}</span>
                      <span className="average_value">{ERROR.DASH}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="section_container">
              <div className="section_container_header">
                <h4 className="title_xsmall_b">
                  {t('T10082_$month_$fuel', {
                    month: CldrDatetimeFormatter.format(startDate, 'MMM'),
                    fuel: ERROR.DASH + defaultFuelEfficiencySymbol,
                  })}
                </h4>
              </div>
              <div className="statistics_graph_area efficiency_statistics">
                <div className="bar_graph">
                  <div className="bar_graph_item">
                    <span
                      className="graph_bar"
                      style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
                    />
                    <span className="graph_title">{t('T10084')}</span>
                  </div>
                  <div className="bar_graph_item">
                    <span
                      className="graph_bar"
                      style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
                    />
                    <span className="graph_title">{t('T10085_$percent', {percent: 10})}</span>
                  </div>
                  <div className="bar_graph_item is-active">
                    <span className="speech_bubble">{ERROR.DASH}</span>
                    <span
                      className="graph_bar"
                      style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
                    />
                    <span className="graph_title">{t('T10086')}</span>
                  </div>
                  <div
                    className="bar_graph_average"
                    style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.AREA_HALF}px`}}
                  >
                    <span className="average_line" />
                    <span className="average_text_box">
                      <span className="average_text">{t('T10083')}</span>
                      <span className="average_value">{ERROR.DASH}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
